import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";

const EMAIL = "hello@MyAE.co.uk";

const H = ({ children }) => <h2 className="font-serif text-2xl tracking-tight mt-8 mb-2 text-foreground">{children}</h2>;
const P = ({ children }) => <p className="text-[15px] leading-[1.8] text-muted-foreground mb-3">{children}</p>;

export default function Contact() {
  const navigate = useNavigate();
  const links = [["privacy", "Privacy Policy"], ["terms", "Terms of Service"], ["cookies", "Cookie Policy"], ["contact", "Contact"]];

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      <div className="border-b border-border bg-card px-6 md:px-8 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm text-oxford hover:underline" data-testid="contact-back-btn">
          <ArrowLeft size={16} /> Back to MyAE
        </button>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-oxford flex items-center justify-center"><span className="font-serif text-white text-base leading-none">Æ</span></div>
          <span className="font-serif text-lg tracking-tight hidden sm:inline">MyAE</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <p className="overline text-oxford mb-2">Get in touch</p>
        <h1 className="font-serif text-5xl tracking-tight leading-none">Contact us</h1>

        <nav className="flex flex-wrap gap-x-5 gap-y-1 mt-5 mb-8 border-y border-border py-3">
          {links.map(([slug, label]) => (
            <Link key={slug} to={`/${slug}`} data-testid={`legal-link-${slug}`}
              className={`text-sm ${slug === "contact" ? "text-oxford font-semibold" : "text-muted-foreground hover:text-oxford"}`}>
              {label}
            </Link>
          ))}
        </nav>

        <article>
          <P>
            Have a question, need help with your account, or want to share feedback about your examiner report?
            We'd love to hear from you. Our team reads every message and typically replies within 1–2 business days.
          </P>

          <div className="mt-6 border border-border rounded-none bg-card p-6" data-testid="contact-email-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-oxford flex items-center justify-center shrink-0">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Email us at</p>
                <a href={`mailto:${EMAIL}`} className="font-serif text-xl tracking-tight text-oxford hover:underline" data-testid="contact-email-link">
                  {EMAIL}
                </a>
              </div>
            </div>
          </div>

          <H>What to include</H>
          <P>
            To help us respond quickly, please tell us your account email (if you have one) and, where relevant,
            the assignment or report you're asking about. For account deletion or data requests, just let us know
            and we'll take care of it.
          </P>

          <H>Billing &amp; subscriptions</H>
          <P>
            Questions about your Pro subscription, credits, or a payment? Email us at {" "}
            <a href={`mailto:${EMAIL}`} className="text-oxford hover:underline">{EMAIL}</a> and include the email
            address linked to your account so we can find your billing profile.
          </P>
        </article>

        <p className="text-xs text-muted-foreground mt-12 border-t border-border pt-4">
          MyAE provides indicative, AI-assisted feedback and is not affiliated with any university.
        </p>
      </div>
    </div>
  );
}
